import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  PlayCircle,
  LogOut,
  Library,
  GraduationCap,
  Compass,
  BarChart3,
  Cpu,
  FolderOpen,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import PortalGate from "@/components/PortalGate";
import VideoPlayer from "@/components/VideoPlayer";
import { portalApi, portalAuth } from "@/lib/portal";

// Curriculum modules — videos load from the backend; modules without videos display as "Coming soon"
const MODULES = [
  {
    key: "Professional Service Projects Capability Building",
    title: "Professional Service Projects Capability Building",
    description:
      "An exclusive capability building series led by Mr. Utkarsh Saxena — practical insights from real Anila Consulting engagements across SAP, AI, and enterprise transformation.",
    icon: GraduationCap,
  },
];

const formatDuration = (seconds) => {
  if (!seconds || seconds < 1) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const formatBytes = (bytes) => {
  if (!bytes) return null;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
};

const Portal = () => {
  const [authed, setAuthed] = useState(portalAuth.isAuthed());
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  const loadVideos = async () => {
    setLoading(true);
    try {
      const { data } = await portalApi.get("/portal/videos");
      setVideos(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
      } else {
        toast.error("Could not load capability building library.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) loadVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleSignOut = () => {
    portalAuth.clear();
    setAuthed(false);
    setVideos([]);
    toast.success("Signed out of capability building portal.");
  };

  const videosByCategory = useMemo(() => {
    const map = {};
    for (const v of videos) {
      const key = v.category || "Uncategorized";
      if (!map[key]) map[key] = [];
      map[key].push(v);
    }
    return map;
  }, [videos]);

  if (!authed) {
    return <PortalGate onAuthed={() => setAuthed(true)} />;
  }

  return (
    <div data-testid="portal-page">
      {/* HERO */}
      <section
        data-testid="portal-hero"
        className="bg-[#111111] text-white pt-32 pb-20 lg:pt-40 lg:pb-24 relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, #8B0000 0px, transparent 40%), radial-gradient(circle at 80% 70%, #F5F5DC 0px, transparent 35%)",
            }}
          />
        </div>
        <div className="relative container-pro grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="overline mb-6">CEO Capability Building Portal</p>
            <h1 className="font-display text-5xl lg:text-7xl leading-[1.05] text-white">
              Learn SAP from
              <br />
              <span className="text-[#8B0000]">industry leaders.</span>
            </h1>
            <p className="mt-8 text-lg text-white/80 leading-relaxed max-w-2xl">
              Gain practical SAP knowledge, real-world implementation
              experience, and insights into AI-driven enterprise
              transformation through exclusive capability building sessions
              led by the leadership team at Anila Consulting.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col items-start lg:items-end gap-4">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/60">
              <span className="w-2 h-2 bg-[#8B0000] rounded-full" />
              Session secure · 12h
            </div>
            {portalAuth.isAdmin() && (
              <Link
                to="/portal/admin"
                data-testid="go-to-admin-link"
                className="btn-ghost-light"
              >
                Admin Console
              </Link>
            )}
            <button
              data-testid="portal-signout-button"
              onClick={handleSignOut}
              className="btn-ghost-light"
            >
              Sign out <LogOut size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section data-testid="portal-curriculum" className="section bg-[#F5F5DC]">
        <div className="container-pro">
          <div className="flex items-center gap-3 mb-12">
            <Library size={26} strokeWidth={1.5} className="text-[#8B0000]" />
            <p className="overline">Video Capability Building Library</p>
          </div>

          <div className="space-y-12">
            {MODULES.map((mod, idx) => {
              const Icon = mod.icon;
              const moduleVideos = videosByCategory[mod.key] || [];
              return (
                <article
                  key={mod.key}
                  data-testid={`portal-module-${mod.key}`}
                  className="bg-white border border-[#E5E5E5] p-8 lg:p-10 fade-up"
                  style={{ animationDelay: `${idx * 70}ms` }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-4">
                      <Icon
                        size={28}
                        strokeWidth={1.5}
                        className="text-[#8B0000] mb-4"
                      />
                      <p className="overline text-[10px] mb-3">
                        {`Module 0${idx + 1}`}
                      </p>
                      <h2 className="font-display text-2xl lg:text-3xl text-[#111111] leading-tight mb-4">
                        {mod.title}
                      </h2>
                      <p className="text-sm text-[#4A4A4A] leading-relaxed">
                        {mod.description}
                      </p>
                    </div>

                    <div className="lg:col-span-8">
                      {moduleVideos.length === 0 ? (
                        <div
                          data-testid={`module-empty-${mod.key}`}
                          className="border border-dashed border-[#8B0000]/30 bg-[#FAF7EE] px-6 py-12 flex flex-col items-center justify-center text-center"
                        >
                          <PlayCircle
                            size={36}
                            strokeWidth={1.2}
                            className="text-[#8B0000]/40 mb-3"
                          />
                          <p className="font-display text-xl text-[#111111] mb-2">
                            Coming soon
                          </p>
                          <p className="text-sm text-[#4A4A4A] max-w-md leading-relaxed">
                            Sessions for this module are being recorded.
                            They&apos;ll appear here as they&apos;re released.
                          </p>
                        </div>
                      ) : (
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {moduleVideos.map((v) => (
                            <li
                              key={v.id}
                              data-testid={`video-card-${v.id}`}
                              className="group cursor-pointer border border-[#E5E5E5] hover:border-[#8B0000] transition-colors duration-300"
                              onClick={() => setActive(v)}
                            >
                              <div className="aspect-video bg-[#111111] relative overflow-hidden flex items-center justify-center">
                                <div
                                  className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-opacity duration-300"
                                  style={{
                                    background:
                                      "linear-gradient(135deg, #1a1a1a 0%, #8B0000 200%)",
                                  }}
                                />
                                <PlayCircle
                                  size={48}
                                  strokeWidth={1.2}
                                  className="text-white/95 relative z-10 transition-transform duration-300 group-hover:scale-110"
                                />
                                {formatDuration(v.duration_seconds) && (
                                  <span className="absolute bottom-2 right-2 text-[10px] uppercase tracking-[0.15em] bg-black/70 text-white px-2 py-1">
                                    {formatDuration(v.duration_seconds)}
                                  </span>
                                )}
                              </div>
                              <div className="p-4">
                                <h3 className="font-display text-lg text-[#111111] leading-snug mb-1 group-hover:text-[#8B0000] transition-colors duration-300">
                                  {v.title}
                                </h3>
                                {v.description && (
                                  <p className="text-xs text-[#4A4A4A] line-clamp-2 leading-relaxed mb-2">
                                    {v.description}
                                  </p>
                                )}
                                {formatBytes(v.size_bytes) && (
                                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#4A4A4A]/70">
                                    {formatBytes(v.size_bytes)}
                                  </p>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {loading && (
            <p
              data-testid="portal-loading"
              className="text-sm text-[#4A4A4A] mt-8"
            >
              Loading library…
            </p>
          )}
        </div>
      </section>

      {active && (
        <VideoPlayer video={active} onClose={() => setActive(null)} />
      )}
    </div>
  );
};

export default Portal;
